"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _url = _interopRequireDefault(require("url"));

var _cookie = _interopRequireDefault(require("cookie"));

var _util = _interopRequireDefault(require("util"));

var _directBinaryUploadController = _interopRequireDefault(require("./direct-binary-upload-controller"));

var _utils = require("./utils");

var _constants = require("./constants");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

/**
 * Options that generally control how a direct binary upload will behave. The class contains
 * several optional configurations, but the minimal setup will require at least the following:
 *
 * ```js
 * const options = new DirectBinaryUploadOptions()
 *  .withUrl(uploadTargetUrl) // URL of the target of the upload
 *  .withUploadFiles(fileList) // list of files to upload
 * ```
 *
 * All other options are optional.
 */
var DirectBinaryUploadOptions = /*#__PURE__*/function () {
  function DirectBinaryUploadOptions() {
    _classCallCheck(this, DirectBinaryUploadOptions);

    this.options = {
      maxConcurrent: _constants.DefaultValues.MAX_CONCURRENT,
      headers: {},
      retryCount: _constants.DefaultValues.RETRY_COUNT,
      retryDelay: _constants.DefaultValues.RETRY_DELAY,
      requestTimeout: _constants.DefaultValues.REQUEST_TIMEOUT
    };
    this.controller = new _directBinaryUploadController.default();
  }
  /**
   * Sets the URL to which binaries will be uploaded. The URL should include the path to
   * a directory. Depending on the context, this could be either a relative or absolute URL.
   * For example, running from a node.js process will require an absolute URL, whereas running
   * from a browser will allow relative URLs.
   *
   * @param {string} url The URL to which binaries will be uploaded.
   * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
   */


  _createClass(DirectBinaryUploadOptions, [{
    key: "withUrl",
    value: function withUrl(url) {
      this.options.url = url;
      return this;
    }
    /**
     * An array of object instances representing the files that will be uploaded
     * to the target URL.
     *
     * @param {Array} uploadFiles Files to upload to the target. Each file must contain
     *  at least the following properties: fileName, fileSize, and either filePath OR blob.
     *  See UploadFile for more information.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withUploadFiles",
    value: function withUploadFiles(uploadFiles) {
      this.options.uploadFiles = uploadFiles;
      return this;
    }
    /**
     * If specified, an object containing headers that will be sent along with each
     * request submitted to the target instance.
     *
     * The given headers will be merged with any headers specified previously using
     * the method.
     *
     * @param {object} headers Keys should be header names, values should be the header's value.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withHeaders",
    value: function withHeaders(headers) {
      this.options.headers = _objectSpread({}, this.options.headers, {}, headers);
      return this;
    }
    /**
     * The given cookies will be merged with any cookies specified previously using the
     * method.
     *
     * @param {object} cookies Keys should be cookie names, values should be the cookie's value.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withCookies",
    value: function withCookies(cookies) {
      var headers = this.getHeaders() || {};

      var existingCookies = _cookie.default.parse(headers.Cookie || headers.cookie || '');

      var cookieString = '';
      Object.keys(existingCookies).forEach(function (toSerialize) {
        if (!cookies[toSerialize]) {
          if (cookieString) {
            cookieString += '; ';
          }

          cookieString += _cookie.default.serialize(toSerialize, existingCookies[toSerialize]);
        }
      });
      Object.keys(cookies).forEach(function (toSerialize) {
        if (cookieString) {
          cookieString += '; ';
        }

        cookieString += _cookie.default.serialize(toSerialize, cookies[toSerialize]);
      });
      return this.withHeaders({
        Cookie: cookieString
      });
    }
    /**
     * Convenience method that adds a basic Authorization header that will be submitted
     * to the target.
     *
     * @param {string} basicAuth Basic authorization value in the form of username:password.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withBasicAuth",
    value: function withBasicAuth(basicAuth) {
      return this.withHeaders({
        'Authorization': "Basic ".concat(Buffer.from(basicAuth).toString('base64'))
      });
    }
    /**
     * Specifies whether or not the process will upload the files concurrently. If false, the
     * process will upload one file at a time. If true, the process will upload all files in
     * a concurrent "thread"-like manner. Default value is true.
     *
     * This is a convenience function that is wrapped around setting withMaxConcurrent() to
     * either 1 (if isConcurrent is false) or its default value (if isConcurrent is true).
     *
     * @param {boolean} isConcurrent True if the process should upload files concurrently, false if
     *  they should be uploaded serially.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withConcurrent",
    value: function withConcurrent(isConcurrent) {
      if (isConcurrent) {
        return this.withMaxConcurrent(5);
      } else {
        return this.withMaxConcurrent(1);
      }
    }
    /**
     * Specifies the maximum number of HTTP requests to invoke at one time. Once this number of pending
     * requests is reached, no more requests will be submitted until at least one of the pending
     * requests finishes. A value less than 2 indicates that only one request at a time is allowed,
     * meaning that files will be uploaded serially instead of concurrently. Default value is 5.
     *
     * @param {number} maxConcurrent Maximum number of pending HTTP requests to allow.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withMaxConcurrent",
    value: function withMaxConcurrent(maxConcurrent) {
      this.options.maxConcurrent = maxConcurrent;
      return this;
    }
    /**
     * DEPRECATED: This method has been deprecated and should no longer be used. The library
     * will now automatically determine if a content length header is needed. The value will
     * be ignored.
     *
     * If true, the process will manually add a "Content-Length" header to requests that upload a
     * file's chunks. If false, the process will assume the header is not needed. The purpose of
     * this option is primarily to support browser upload cases, which won't require this process
     * to add the header. Default: true.
     *
     * @param {boolean} doAddContentLengthHeader True if the process should add its own "Content-Length"
     *  header value.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withAddContentLengthHeader",
    value: function withAddContentLengthHeader() {
      var withAddContentLengthHeaderDeprecated = _util.default.deprecate(function () {}, 'withAddContentLengthHeader is deprecated and no longer required.');

      withAddContentLengthHeaderDeprecated();
      return this;
    }
    /**
     * Specifies the number of times the process will attempt retrying a failed HTTP request before
     * giving up and reporting an error. Default: 3.
     *
     * @param {number} retryCount Number of times to resubmit a request.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withHttpRetryCount",
    value: function withHttpRetryCount(retryCount) {
      this.options.retryCount = retryCount;
      return this;
    }
    /**
     * Sets the amount of time, in milliseconds, that the process will wait before retrying a
     * failed HTTP request. The delay will increase itself by this value each time the failed
     * request is resubmitted. For example, if the delay is 5,000 then the process will wait
     * 5,000 milliseconds for the first retry, then 10,000, then 15,000, etc. Default: 5,000.
     *
     * @param {number} retryDelay A timespan in milliseconds.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withHttpRetryDelay",
    value: function withHttpRetryDelay(retryDelay) {
      this.options.retryDelay = retryDelay;
      return this;
    }
    /**
     * Sets the maximum amount of time the module will wait for an HTTP request to complete
     * before timing out. Default: 1 minute.
     * @param {number} timeout Timeout duration, in milliseconds.
     * @returns {DirectBinaryUploadOptions} The current options instance. Allows for chaining.
     */

  }, {
    key: "withHttpRequestTimeout",
    value: function withHttpRequestTimeout(timeout) {
      this.options.requestTimeout = timeout;
      return this;
    }
    /**
     * Retrieves the target URL to which files will be uploaded.
     *
     * @returns {string} Target URL as provided to the options instance.
     */

  }, {
    key: "getUrl",
    value: function getUrl() {
      return (0, _utils.trimRight)(this.options.url, ['/']) || '/';
    }
    /**
     * Retrieves the path to the folder where the files will be uploaded. This value
     * is based on the URL that was provided to the options.
     *
     * @returns {string} Full path to a folder on the target.
     */

  }, {
    key: "getTargetFolderPath",
    value: function getTargetFolderPath() {
      var _URL$parse = _url.default.parse(this.getUrl()),
          pathname = _URL$parse.pathname;

      return pathname;
    }
    /**
     * Retrieves the target URL's prefix, which is everything in the URL up to the target path.
     * Will be empty if there is no prefix.
     *
     * @returns {string} The target URL's prefix.
     */

  }, {
    key: "getUrlPrefix",
    value: function getUrlPrefix() {
      var _URL$parse2 = _url.default.parse(this.getUrl()),
          protocol = _URL$parse2.protocol,
          host = _URL$parse2.host;

      return host ? "".concat(protocol, "//").concat(host) : '';
    }
    /**
     * Retrieves the list of files that will be uploaded.
     *
     * @returns {Array} List of UploadFile instances as provided to the options instance.
     */

  }, {
    key: "getUploadFiles",
    value: function getUploadFiles() {
      return this.options.uploadFiles || [];
    }
    /**
     * Retrieves the headers that will be added to each request sent to the target
     * instance.
     *
     * @returns {object} The headers as provided to the options instance.
     */

  }, {
    key: "getHeaders",
    value: function getHeaders() {
      return this.options.headers || {};
    }
    /**
     * Retrieves a value indicating whether or not the upload process will transfer files
     * concurrently.
     *
     * @returns {boolean} The value as provided to the options instance.
     */

  }, {
    key: "isConcurrent",
    value: function isConcurrent() {
      return this.getMaxConcurrent() > 1;
    }
    /**
     * Retrieves the maximum number of concurrent HTTP requests that should be allowed.
     *
     * @returns {number} Maximum number.
     */

  }, {
    key: "getMaxConcurrent",
    value: function getMaxConcurrent() {
      return this.options.maxConcurrent;
    }
    /**
     * DEPRECATED: This method has been deprecated and should no longer be used. The library
     * will now automatically determine if a content length header is needed.
     *
     * Retrieves a value indicating whether or not the upload process will add its own
     * Content-Length header to file chunk requests.
     *
     * @returns {boolean} The value as provided to the options instance.
     */

  }, {
    key: "addContentLengthHeader",
    value: function addContentLengthHeader() {
      return false;
    }
    /**
     * Retrieves an object that can be used to control various aspects of the upload process,
     * including cancelling uploads.
     *
     * @returns {DirectBinaryUploadController} Controller for the upload process.
     */

  }, {
    key: "getController",
    value: function getController() {
      return this.controller;
    }
    /**
     * Retrieves the number of times the process will attempt to resubmit a failed HTTP request.
     *
     * @returns {number} Retry count.
     */

  }, {
    key: "getHttpRetryCount",
    value: function getHttpRetryCount() {
      return this.options.retryCount;
    }
    /**
     * Retrieves the amount of time, in milliseconds, the process wil wait between resubmitting
     * the same failed HTTP request.
     *
     * @returns {number} Timespan in milliseconds.
     */

  }, {
    key: "getHttpRetryDelay",
    value: function getHttpRetryDelay() {
      return this.options.retryDelay;
    }
    /**
     * Retrieves the maximum amount of time that the module will wait for an HTTP request to
     * complete before timing out.
     *
     * @returns {number} Timeout duration, in milliseconds.
     */

  }, {
    key: "getHttpRequestTimeout",
    value: function getHttpRequestTimeout() {
      return this.options.requestTimeout;
    }
    /**
     * Overridden to return an object appropriate for representing this class as a
     * JSON object.
     *
     * @returns {object} The class's JSON representation.
     */

  }, {
    key: "toJSON",
    value: function toJSON() {
      return this.options;
    }
  }]);

  return DirectBinaryUploadOptions;
}();

var _default = DirectBinaryUploadOptions;
exports.default = _default;